import type { CampaignQuiz } from '@/services/api/types';

export const DEFAULT_MATCH_QUESTIONS = 10;

export type ExtendedCampaignQuiz = CampaignQuiz & {
  quiz: CampaignQuiz['quiz'] & {
    categories: string[];
  };
};

export type TeamId = 'teletekst' | 'trending';

export type GameQuestion = {
  id: string;
  text: string;
  options: { id: string; text: string }[];
  team: TeamId;
};

const isValidQuiz = (quiz: ExtendedCampaignQuiz): boolean =>
  !!quiz.quiz.question && !!quiz.quiz.answerOptions?.length && !!quiz.quiz.categories?.length;

const pickRandom = <T,>(items: T[], count: number): T[] => {
  const copy = items.slice();
  const result: T[] = [];
  const limit = Math.min(count, copy.length);

  for (let i = 0; i < limit; i += 1) {
    const idx = Math.floor(Math.random() * copy.length);
    result.push(copy.splice(idx, 1)[0]);
  }

  return result;
};

const shuffle = <T,>(items: T[]): T[] => {
  const copy = items.slice();

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
};

const toGameQuestion = (quiz: ExtendedCampaignQuiz, team: TeamId): GameQuestion => ({
  id: quiz.quiz.id,
  text: quiz.quiz.question ?? '',
  options: (quiz.quiz.answerOptions ?? []).map(option => ({
    id: option.id,
    text: option.answer
  })),
  team
});

export const createQuizQueue = (
  quizzes: CampaignQuiz[],
  total = DEFAULT_MATCH_QUESTIONS
): GameQuestion[] => {
  if (!quizzes.length || total <= 0) return [];

  const extendedQuizzes = quizzes as ExtendedCampaignQuiz[];
  const validQuizzes = extendedQuizzes.filter(isValidQuiz);
  
  const teletekstQuizzes = validQuizzes.filter(quiz => quiz.quiz.categories?.[0] === 'teletekst');
  const trendingQuizzes = validQuizzes.filter(quiz => quiz.quiz.categories?.[0] === 'trending');
  
  const questionsPerTeam = Math.ceil(total / 2);
  
  const pickedTeletekst = pickRandom(teletekstQuizzes, questionsPerTeam);
  const pickedTrending = pickRandom(trendingQuizzes, questionsPerTeam);
  
  const shuffledTeletekst = shuffle(pickedTeletekst);
  const shuffledTrending = shuffle(pickedTrending);
  
  const queue: GameQuestion[] = [];
  let teletekstIndex = 0;
  let trendingIndex = 0;
  let lastTeam: TeamId | null = null;
  
  while (queue.length < total) {
    const canUseTeletekst = teletekstIndex < shuffledTeletekst.length;
    const canUseTrending = trendingIndex < shuffledTrending.length;
    
    if (!canUseTeletekst && !canUseTrending) break;
    
    if (lastTeam === 'teletekst' && canUseTrending) {
      queue.push(toGameQuestion(shuffledTrending[trendingIndex], 'trending'));
      trendingIndex++;
      lastTeam = 'trending';
    } else if (lastTeam === 'trending' && canUseTeletekst) {
      queue.push(toGameQuestion(shuffledTeletekst[teletekstIndex], 'teletekst'));
      teletekstIndex++;
      lastTeam = 'teletekst';
    } else if (canUseTeletekst) {
      queue.push(toGameQuestion(shuffledTeletekst[teletekstIndex], 'teletekst'));
      teletekstIndex++;
      lastTeam = 'teletekst';
    } else if (canUseTrending) {
      queue.push(toGameQuestion(shuffledTrending[trendingIndex], 'trending'));
      trendingIndex++;
      lastTeam = 'trending';
    }
  }
  
  return queue;
};

